<template>
	<div id="mf-login" class="o-page-wrap mx-auto min-h-[70rem] w-full max-w-[490px]">
		<h1 class="ocm-headline2 mb-6 pt-8">{{ t("lastStep.headline") }}</h1>

		<SharedForm
			class="ocm-body1 mb-15 mt-4"
			no-submit-button-gap
			novalidate="novalidate"
			:error-message="backendErrorMessage || t('lastStep.required_error_message')"
			:show-error="!!backendErrorMessage ? true : undefined"
			@change="backendErrorMessage = undefined"
			@submit="submitForm"
		>
			<SharedRadioGroup
				v-model="salutation"
				name="gender"
				:display-name="t('lastStep.salutation_display_name')"
				:label="`${t('lastStep.salutation_label')} ${t('lastStep.required_label')}`"
				:required-error-message="t('lastStep.salutation_error_message')"
				inner-class="mt-4 flex gap-4"
				required
				@change-error-msg="(_, data) => handleSalutationChange(data.errorMessage)"
			>
				<SharedRadio radio-value="mrs">{{ t("lastStep.salutation_ms") }}</SharedRadio>
				<SharedRadio radio-value="mr">{{ t("lastStep.salutation_mr") }}</SharedRadio>
			</SharedRadioGroup>

			<p id="country_desc_1" class="mb-5 mt-6">{{ t("lastStep.which_country") }}</p>

			<SharedDropdown
				v-model="country"
				name="originCountryCode"
				:display-name="t('lastStep.country_display_name')"
				:placeholder="`${t('lastStep.country_placeholder')}`"
				:label="`${t('lastStep.country_display_name')} ${t('lastStep.required_label')}`"
				:options="selectableCountryItems"
				:image-urls="flagURLs"
				required
				:aria-describedby="'country_desc_1' + (!isDeliveryCountry ? ' country_desc_2' : '')"
			/>

			<div v-if="!isDeliveryCountry" class="mt-4 flex text-gray-70" data-test-address-country-info>
				<SvgIcon class="mr-2 size-5 flex-none p-px" icon-name="info" />
				<p id="country_desc_2">{{ t("lastStep.shipping_only_in_germany") }}</p>
			</div>

			<i18n-t keypath="lastStep.legal" tag="p" class="ocm-subtext1 my-6">
				<template #clubTerms>
					<a :href="clubTermsUrl" class="underline" target="_blank">{{ t("lastStep.clubTerms") }}</a>
				</template>
				<template #clubTerms2>
					<a :href="clubTermsUrl" class="underline" target="_blank">{{ t("lastStep.clubTerms2") }}</a>
				</template>
				<template #dataProtection>
					<a :href="dataProtectionUrl" class="underline" target="_blank">{{ t("lastStep.dataProtection") }}</a>
				</template>
			</i18n-t>

			<template #submitButton>
				<SharedButton type="submit" class="w-full" :is-loading="isLoading" @click="trackSubmit">
					{{ t("lastStep.submit") }}
				</SharedButton>
			</template>
		</SharedForm>
	</div>
</template>

<script setup lang="ts">
import SharedButton from "@SharedVueComponents/SharedButton.vue";
import SharedDropdown from "@SharedVueComponents/SharedDropdown.vue";
import SharedForm from "@SharedVueComponents/SharedForm/SharedForm.vue";
import SharedRadio from "@SharedVueComponents/SharedRadio/SharedRadio.vue";
import SharedRadioGroup from "@SharedVueComponents/SharedRadio/SharedRadioGroup.vue";
import SvgIcon from "@SharedVueComponents/SvgIcon/SvgIcon.vue";
import { keepFooterOnBottom } from "@SharedVueComposables/use-footer";
import { language } from "@ocm/services/ocm-object";
import { $axios } from "@ocm/axios";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { pushEvent } from "@/services/dataLayerLogin";
import { RegisterResponseData, RegisterSubmitError } from "@/types/register.type";
import OcmMessenger from "ocm_mercurius_messenger";
import { messengerPublicationTypes } from "@ocm/services/services.constants";
import { getDepartmentCookie } from "@ocm/services/departmentCookie";

const { t } = useI18n();

const confirmationPage = window.ocm?.config?.confirmationPage;

const salutation = ref<string | null>(null);
const backendErrorMessage = ref<string>();
const isLoading = ref(false);
const ACTIVATED_DELIVERY_COUNTRIES = confirmationPage?.activatedDeliveryCountries || [];
const isDeliveryCountry = computed(() => ACTIVATED_DELIVERY_COUNTRIES.includes(country.value || ""));
const hasDepartmentCookieOnload = !!getDepartmentCookie();
const selectableCountryItems = transformSelectableCountries(confirmationPage?.selectableCountries);
const country = ref<string | undefined>(getPreselectedCountry());
const flagBaseUrl = "/_ui/responsive/common/images/flags/";
const flagURLs = getFlagURLs(confirmationPage?.selectableCountries);

keepFooterOnBottom();

function getPreselectedCountry() {
	if (!confirmationPage?.selectableCountries) return;
	const preselectedCountry = confirmationPage?.preferredCountryIsoCode;
	if (!preselectedCountry || !selectableCountryItems[preselectedCountry]) {
		return confirmationPage.selectableCountries[0].isocode;
	}
	return preselectedCountry;
}

async function submitForm(event: Event) {
	isLoading.value = true;
	try {
		const form = getFormFromEvent(event);
		if (!form) return;

		if (!hasDepartmentCookieOnload) sendChangeDepartment(salutation.value || "mr");

		const response = await postFormData(form);
		handleSubmitResponse(response.data);
	} catch (err) {
		handleSubmitError(err as RegisterSubmitError);
	}
}

function getFormFromEvent(event: Event): HTMLFormElement | null {
	return (event.target as HTMLElement).closest("form");
}

async function postFormData(form: HTMLFormElement) {
	const requestUrl = confirmationPage?.formAction || "";

	return $axios.post(requestUrl, getEncodedFormData(form), {
		params: getUrlQueryParams(),
	});
}

function getEncodedFormData(form: HTMLFormElement): string {
	const formData = new FormData(form);
	const formEncoded = new URLSearchParams();

	formData.forEach((value, key) => {
		if (typeof value === "string") {
			formEncoded.append(key, value);
		}
	});

	return formEncoded.toString();
}

function getUrlQueryParams(): Record<string, string> {
	return Object.fromEntries(new URLSearchParams(window.location.search));
}

function handleSubmitResponse(data: RegisterResponseData) {
	if (data.httpStatus === 200) {
		window.location = data.redirectUrl;
	} else {
		handleError();
	}
}

function handleSubmitError(error: RegisterSubmitError): void {
	const errorMessages = error?.response?.data?.globalErrorMessages;
	handleError(errorMessages?.[0]);
}

function handleError(newErrorMessage?: string | undefined) {
	backendErrorMessage.value = newErrorMessage || t("lastStep.default_backend_error_message");
	trackBackendError(backendErrorMessage.value);
	isLoading.value = false;
}

function transformSelectableCountries(countries: SelectableCountry[] | undefined) {
	if (!countries) return {};
	return countries.reduce((acc: SelectableDropdownCountryItem, country: SelectableCountry) => {
		acc[country.isocode] = country.name;
		return acc;
	}, {});
}

function getFlagURLs(countries: SelectableCountry[] | undefined) {
	if (!countries) return [];
	return countries.map((country: SelectableCountry) => flagBaseUrl + country.flag, {});
}

watch(country, () => {
	trackCountryChange();
});

const dataProtectionUrl = language === "de" ? "/de/metzingen/datenschutz/" : "/en/metzingen/data-protection/";
const clubTermsUrl = language === "de" ? "/de/metzingen/club-bedingungen/" : "/en/metzingen/club-terms/";

function handleSalutationChange(errorMessage?: string) {
	if (errorMessage) trackSalutationError();
}

function trackSubmit() {
	pushEvent("RegisterButton");
}

function trackCountryChange() {
	pushEvent("change_country");
}

function trackSalutationError() {
	pushEvent("submit_salutation_error");
}

function trackBackendError(errorMessage: string) {
	pushEvent("submit_backend_error", errorMessage);
}

const SALUTATION_TO_DEPARTMENT_MAP: Record<string, string> = {
	mr: "men",
	mrs: "women",
};

function sendChangeDepartment(salutation: string) {
	const department = SALUTATION_TO_DEPARTMENT_MAP[salutation] || "";
	OcmMessenger.publish(messengerPublicationTypes.DEPARTMENT_CHANGE, department);
}
</script>
